import { useAuth } from '../auth'

const CLEAR_SUBSCRIPTIONS_KEY = 'storage-clear-subscriptions'

export interface ClearSubscription {
  key: string
  global: boolean
}

export class PersistService {
  private backupUserId?: number | string
  private backupTenantUuid?: string

  constructor() {
    // Only add event listener in browser environment
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageEvent)
    }
  }

  private readonly handleStorageEvent = (event: StorageEvent) => {
    if (event.key === 'authInfo') {
      this.doClearSubscriptions()
    }
  }

  private readonly doClearSubscriptions = () => {
    const subs = this.getGlobal<ClearSubscription[]>(CLEAR_SUBSCRIPTIONS_KEY)
    if (subs) {
      subs.forEach((subscription) => {
        this.clear(subscription.key, subscription.global)
      })
      this.clear(CLEAR_SUBSCRIPTIONS_KEY, true)
    }
  }

  generateUuid(): string {
    // Check if we're in a browser environment
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.getRandomValues) {
      // Fallback for server-side environment
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
      })
    }

    const buffer = new Uint8Array(16)
    window.crypto.getRandomValues(buffer)

    // Set version (4) and variant (RFC4122)
    buffer[6] = (buffer[6] & 0x0f) | 0x40
    buffer[8] = (buffer[8] & 0x3f) | 0x80

    const hex = Array.from(buffer)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20, 32)].join('-')
  }

  getGlobal<T>(key: string): T | null {
    return this.get<T>(this.getStorageKeyGlobal(key))
  }

  setGlobal<T>(key: string, value: T): void {
    this.set(this.getStorageKeyGlobal(key), value)
  }

  getRouteScoped<T>(key: string, routeInfo?: { path: string; params?: Record<string, string> }): T | null {
    return this.get<T>(this.getStorageKey(key, routeInfo))
  }

  setRouteScoped<T>(key: string, value: T, routeInfo?: { path: string; params?: Record<string, string> }): void {
    this.set(this.getStorageKey(key, routeInfo), value)
  }

  getBasic<T>(key: string): T | null {
    return this.get<T>(key)
  }

  setBasic<T>(key: string, value: T): void {
    this.set(key, value)
  }

  clear(key: string, isGlobal?: boolean, routeInfo?: { path: string; params?: Record<string, string> }): void {
    if (typeof localStorage === 'undefined') return
    const storageKey = isGlobal ? this.getStorageKeyGlobal(key) : this.getStorageKey(key, routeInfo)
    localStorage.removeItem(storageKey)
  }

  private get<T>(storageKey: string): T | null {
    if (typeof localStorage === 'undefined') return null
    const val = localStorage.getItem(storageKey)
    if (!val) return null

    try {
      return JSON.parse(val) as T
    } catch (e) {
      console.error('Error parsing localStorage value:', e)
      return null
    }
  }

  private set<T>(storageKey: string, value: T): void {
    if (typeof localStorage === 'undefined') return
    try {
      const json = JSON.stringify(value)
      localStorage.setItem(storageKey, json)
    } catch (e) {
      console.error('Error setting localStorage value:', e)
    }
  }

  private getStorageKey(key: string, routeInfo?: { path: string; params?: Record<string, string> }): string {
    const userId = this.getUserId()
    const tenantUuid = this.getTenantUuid()

    let routePart = ''
    if (routeInfo) {
      const { path, params } = routeInfo
      const routePath = path.replace(/^\//, '')

      routePart = `_${routePath}`

      if (params) {
        const paramKeys = Object.keys(params)
        if (paramKeys.length > 0) {
          const paramValues = paramKeys.map((k) => params[k]).join('_')
          routePart += `_${paramValues}`
        }
      }
    }

    return `${userId}_${tenantUuid}${routePart}_${key}`
  }

  private getStorageKeyGlobal(key: string): string {
    const userId = this.getUserId()
    const tenantUuid = this.getTenantUuid()
    return `${userId}_${tenantUuid}_${key}`
  }

  private getUserId(): string | number {
    if (typeof localStorage === 'undefined') return 'anonymous'
    try {
      const authInfo = localStorage.getItem('authInfo')
      if (authInfo) {
        const parsed = JSON.parse(authInfo)
        if (parsed.user?.id) {
          return parsed.user.id
        }
      }
    } catch (e) {
      console.error('Error getting user ID:', e)
    }

    return this.backupUserId ?? 'anonymous'
  }

  private getTenantUuid(): string {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return 'default'
    try {
      const urlParams = new URLSearchParams(window.location.search)
      const urlTenantUuid = urlParams.get('tenantUuid')
      if (urlTenantUuid) {
        localStorage.setItem('tenantUuid', urlTenantUuid)
        return urlTenantUuid
      }

      const storedTenantUuid = localStorage.getItem('tenantUuid')
      if (storedTenantUuid) {
        return storedTenantUuid
      }

      const authInfo = localStorage.getItem('authInfo')
      if (authInfo) {
        const parsed = JSON.parse(authInfo)
        if (parsed.user?.tenantUuid) {
          return parsed.user.tenantUuid
        }
      }
    } catch (e) {
      console.error('Error getting tenant UUID:', e)
    }

    return this.backupTenantUuid ?? 'default'
  }

  clearAtLogout(key: string, isGlobal?: boolean): void {
    this.backupUserId = this.getUserId()
    this.backupTenantUuid = this.getTenantUuid()

    const subs = this.getGlobal<ClearSubscription[]>(CLEAR_SUBSCRIPTIONS_KEY) ?? []

    const existingSub = subs.find((sub) => sub.key === key)
    if (!existingSub) {
      subs.push({
        key,
        global: !!isGlobal
      })

      this.setGlobal(CLEAR_SUBSCRIPTIONS_KEY, subs)
    }
  }
}

export const persistService = new PersistService()

export function usePersist() {
  useAuth()

  return {
    getGlobal: persistService.getGlobal.bind(persistService),
    setGlobal: persistService.setGlobal.bind(persistService),
    getRouteScoped: persistService.getRouteScoped.bind(persistService),
    setRouteScoped: persistService.setRouteScoped.bind(persistService),
    getBasic: persistService.getBasic.bind(persistService),
    setBasic: persistService.setBasic.bind(persistService),
    clear: persistService.clear.bind(persistService),
    clearAtLogout: persistService.clearAtLogout.bind(persistService),
    generateUuid: persistService.generateUuid.bind(persistService)
  }
}

export default persistService
